import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import posthog from 'posthog-js';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
let analytics = null;
let isInitialized = false;

const initializeAnalytics = () => {
  // Only initialize if we have the required config
  if (!firebaseConfig.apiKey || !firebaseConfig.measurementId) {
    console.warn('Firebase Analytics not configured. Skipping initialization.');
    return null;
  }

  try {
    const app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
    isInitialized = true;
    console.log(`✅ Firebase Analytics initialized for: ${firebaseConfig.projectId}`);
    return analytics;
  } catch (error) {
    console.error('Failed to initialize Firebase Analytics:', error);
    return null;
  }
};

// Initialize on import
initializeAnalytics();

// ---------------------------------------------------------------------------
// PostHog (runs ALONGSIDE Firebase; independent of Firebase init state).
// SAFETY: no-ops entirely when REACT_APP_POSTHOG_KEY is unset, and every call
// is wrapped so analytics can never break the app. Removing PostHog later =
// deleting this block + the phCapture lines; no component changes needed.
// ---------------------------------------------------------------------------
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
let posthogReady = false;

const initPostHog = () => {
  if (!POSTHOG_KEY) {
    console.warn('PostHog not configured. Skipping initialization.');
    return;
  }
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false,   // fired manually per route change (see trackPageView)
      capture_pageleave: true,   // time-on-page
      autocapture: true,         // full behavioral picture (clicks, rageclicks)
      persistence: 'localStorage',
      // FULL session recording — masking relaxed to passwords only (retail data, no PHI)
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: { password: true },
      },
    });
    posthogReady = true;
    // Tag EVERY client event with source:'client' (super property), mirroring the
    // server's source:'server'. This makes the 3 events that fire from both sides
    // (user_logged_in, product_deleted, reorder_marked_fulfilled) trivially
    // separable — filter source='server' for authoritative, de-duplicated counts.
    posthog.register({ source: 'client' });
    console.log('✅ PostHog initialized (US cloud)');
  } catch (e) {
    console.error('PostHog init failed:', e);
  }
};

initPostHog();

// Safe capture — never throws, no-ops when PostHog is not ready.
export const phCapture = (event, properties = {}) => {
  if (!posthogReady) return;
  try {
    posthog.capture(event, properties);
  } catch (e) {
    console.error('PostHog capture failed:', e);
  }
};

// Clear identity on logout.
export const resetIdentity = () => {
  if (!posthogReady) return;
  try {
    posthog.reset();
  } catch (e) {
    console.error('PostHog reset failed:', e);
  }
};

// Register a super property that is attached to EVERY subsequent event
// (incl. autocapture + session replay). Used to tag which UI the user is on:
// 'v1' (legacy /dashboard) vs 'v2' (/new/*). Set from the route on each change.
export const setUiVersion = (version) => {
  if (!posthogReady) return;
  try {
    posthog.register({ ui_version: version });
  } catch (e) {
    console.error('PostHog setUiVersion failed:', e);
  }
};

// Track page views
export const trackPageView = (pageName, additionalParams = {}) => {
  phCapture('$pageview', { page_name: pageName, ...additionalParams });

  if (!isInitialized) return;

  try {
    logEvent(analytics, 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...additionalParams
    });
    console.log(`📊 Page view tracked: ${pageName}`);
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track custom events
export const trackEvent = (eventName, params = {}) => {
  phCapture(eventName, params);

  if (!isInitialized) return;

  try {
    logEvent(analytics, eventName, params);
    console.log(`📊 Event tracked: ${eventName}`, params);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Track user login
export const trackLogin = (userId, username, role) => {
  if (posthogReady) {
    try {
      const shop = localStorage.getItem('shop_name') || 'Unknown';
      // distinct_id = numeric user id (matches the server-side events)
      posthog.identify(String(userId), { role, username, shop_name: shop });
      posthog.group('shop', shop);   // shop-level group analytics
      phCapture('user_logged_in', { role });
    } catch (e) {
      console.error('PostHog login tracking failed:', e);
    }
  }

  if (!isInitialized) return;

  try {
    setUserId(analytics, userId.toString());
    setUserProperties(analytics, {
      username: username,
      role: role,
      shop_name: localStorage.getItem('shop_name') || 'Unknown'
    });
    logEvent(analytics, 'login', {
      method: 'jwt',
      user_id: userId,
      role: role
    });
    console.log(`📊 Login tracked for user: ${username}`);
  } catch (error) {
    console.error('Error tracking login:', error);
  }
};

// Track errors
export const trackError = (error, context = '') => {
  // PostHog: 'error' removed — redundant with the more structured 'api_error'
  // event fired alongside it in AxiosInstance.js. Firebase still gets it below.

  if (!isInitialized) return;

  try {
    logEvent(analytics, 'error', {
      error_message: error.message || 'Unknown error',
      error_stack: error.stack || '',
      error_context: context,
      page_path: window.location.pathname
    });
    console.log(`📊 Error tracked: ${error.message}`);
  } catch (err) {
    console.error('Error tracking error:', err);
  }
};

// Track business events
export const trackSaleCreated = (saleData) => {
  phCapture('sale_created', {
    sale_id: saleData.sale_id,
    total_amount: saleData.total_price || 0,
    items_count: saleData.items?.length || 0,
    payment_method: saleData.payment_method || 'unknown',
  });

  if (!isInitialized) return;

  try {
    logEvent(analytics, 'sale_created', {
      sale_id: saleData.sale_id,
      total_amount: saleData.total_price || 0,
      items_count: saleData.items?.length || 0,
      payment_method: saleData.payment_method || 'unknown'
    });
    console.log(`📊 Sale created tracked: ${saleData.sale_id}`);
  } catch (error) {
    console.error('Error tracking sale:', error);
  }
};

export const trackPurchaseCreated = (purchaseData) => {
  phCapture('purchase_created', {
    purchase_id: purchaseData.purchase_id,
    total_amount: purchaseData.total_price || 0,
    items_count: purchaseData.items?.length || 0,
    supplier: purchaseData.supplier_name || 'unknown',
  });

  if (!isInitialized) return;

  try {
    logEvent(analytics, 'purchase_created', {
      purchase_id: purchaseData.purchase_id,
      total_amount: purchaseData.total_price || 0,
      items_count: purchaseData.items?.length || 0,
      supplier: purchaseData.supplier_name || 'unknown'
    });
    console.log(`📊 Purchase created tracked: ${purchaseData.purchase_id}`);
  } catch (error) {
    console.error('Error tracking purchase:', error);
  }
};

export const trackProductAdded = (productData) => {
  phCapture('product_added', {
    product_id: productData.product_id,
    has_image: !!productData.image_url,
  });

  if (!isInitialized) return;

  try {
    logEvent(analytics, 'product_added', {
      product_id: productData.product_id,
      product_name: productData.product_name,
      has_image: !!productData.image_url
    });
    console.log(`📊 Product added tracked: ${productData.product_name}`);
  } catch (error) {
    console.error('Error tracking product:', error);
  }
};

// ---------------------------------------------------------------------------
// PostHog-only event helpers (ZambiaShoppe-specific; no Firebase equivalent).
// Thin wrappers over phCapture so call sites stay clean and consistent.
// ---------------------------------------------------------------------------

// CLIP image-search funnel
export const trackClipSearchStarted   = (inputType) => phCapture('clip_search_started', { input_type: inputType });
export const trackClipSearchCompleted = (resultCount, latencyMs, topScore) =>
  phCapture('clip_search_completed', { result_count: resultCount, latency_ms: latencyMs, top_score: topScore });
export const trackClipResultSelected  = (rankPosition) => phCapture('clip_search_result_selected', { rank_position: rankPosition });
export const trackClipSearchAbandoned = (resultCount) => phCapture('clip_search_abandoned', { result_count: resultCount });

// Product management
export const trackProductSearch        = (queryLength, resultCount) => phCapture('product_search', { query_length: queryLength, result_count: resultCount });
export const trackProductEdited        = (productId) => phCapture('product_edited', { product_id: productId });
export const trackProductDeleted       = (productId) => phCapture('product_deleted', { product_id: productId });
export const trackProductImageUploaded = (success, sizeKb) => phCapture('product_image_uploaded', { success: !!success, size_kb: sizeKb });

// Sales & purchases (edits/deletes; creates use the dual-fire helpers above)
export const trackSaleEdited       = (saleId) => phCapture('sale_edited', { sale_id: saleId });
export const trackSaleDeleted      = (saleId) => phCapture('sale_deleted', { sale_id: saleId });
export const trackPurchaseEdited   = (purchaseId) => phCapture('purchase_edited', { purchase_id: purchaseId });
export const trackPurchaseDeleted  = (purchaseId) => phCapture('purchase_deleted', { purchase_id: purchaseId });

// Inventory & reorder flow
export const trackReorderCandidatesViewed = () => phCapture('reorder_candidates_viewed');
export const trackReorderCreated          = (reorderId) => phCapture('reorder_created', { reorder_id: reorderId });
export const trackReorderMarkedFulfilled  = (reorderId) => phCapture('reorder_marked_fulfilled', { reorder_id: reorderId });
export const trackInventoryAdjusted       = (productId) => phCapture('inventory_adjusted', { product_id: productId });

// Units & conversion
export const trackUnitAdded         = (unitId, category) => phCapture('unit_added', { unit_id: unitId, unit_category: category });
export const trackUnitConversionSet = (productId) => phCapture('unit_conversion_set', { product_id: productId });

// Suppliers
export const trackSupplierAdded         = (supplierId) => phCapture('supplier_added', { supplier_id: supplierId });
export const trackSupplierLinkedToUnit  = (unitId, supplierId) => phCapture('supplier_linked_to_unit', { unit_id: unitId, supplier_id: supplierId });

// Insights
export const trackInsightsViewed         = () => phCapture('insights_viewed');
export const trackInsightsNewBadgeClicked = () => phCapture('insights_new_badge_clicked');

// PWA update flow
export const trackAppUpdateBannerShown = () => phCapture('app_update_banner_shown');
export const trackAppUpdateReloaded    = () => phCapture('app_update_reloaded');

// Connectivity & failures (Zambia: variable connectivity + base64 image uploads)
export const trackImageUploadFailed = (endpoint, reason) => phCapture('image_upload_failed', { endpoint, reason });
export const trackRequestTimeout    = (endpoint) => phCapture('request_timeout', { endpoint });
export const trackApiError          = (status, endpoint) => phCapture('api_error', { status, endpoint });

// Export analytics instance for advanced usage
export { analytics, posthog };
export default analytics;
